import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Target, MagnifyingGlass, CaretDown } from "@phosphor-icons/react";

const STATUS_COLOR = {
  "New": "bg-sky-500/15 text-sky-300",
  "Contacted": "bg-indigo-500/15 text-indigo-300",
  "Appointment Booked": "bg-emerald-500/15 text-emerald-300",
  "Qualified": "bg-teal-500/15 text-teal-300",
  "Proposal Sent": "bg-amber-500/15 text-amber-300",
  "Won": "bg-green-500/20 text-green-300",
  "Lost": "bg-red-500/15 text-red-300",
  "Follow-Up Required": "bg-yellow-500/15 text-yellow-300",
};

function LeadRow({ lead, statuses, refresh }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const setStatus = async (status) => {
    try { await api.patch(`/admin/leads/${lead._id}`, { status }); toast.success(`Marked ${status}`); refresh(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const addNote = async () => {
    if (!note.trim()) return;
    try { await api.patch(`/admin/leads/${lead._id}`, { note }); toast.success("Note added"); setNote(""); refresh(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const appt = lead.appointment;
  return (
    <div className="rounded-xl border border-white/10 bg-card p-4" data-testid={`lead-${lead._id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-semibold">{lead.full_name} {lead.business_name ? <span className="text-muted-foreground">· {lead.business_name}</span> : null}</p>
          <p className="text-xs text-muted-foreground">{lead.industry || "—"} · {lead.email} · {lead.phone}</p>
          <p className="mt-1 text-xs text-muted-foreground">{(lead.services || []).join(", ") || "—"}{lead.other_service ? ` · Other: ${lead.other_service}` : ""}</p>
          {appt && <p className="mt-1 text-xs text-emerald-400">📅 {appt.date} at {appt.time} ({appt.timezone}){lead.google_event_id ? " · Google ✓" : ""}</p>}
        </div>
        <div className="flex flex-col items-end gap-1.5">
          <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold ${STATUS_COLOR[lead.status] || "bg-white/10 text-muted-foreground"}`}>{lead.status}</span>
          <div className="flex items-center gap-1">
            <a href={`tel:${lead.phone}`} className="rounded-lg bg-white/5 px-2 py-1 text-[11px] font-semibold hover:bg-white/10">Call</a>
            <a href={`sms:${lead.phone}`} className="rounded-lg bg-white/5 px-2 py-1 text-[11px] font-semibold hover:bg-white/10">Text</a>
            <a href={`mailto:${lead.email}`} className="rounded-lg bg-white/5 px-2 py-1 text-[11px] font-semibold hover:bg-white/10">Email</a>
            <button data-testid={`lead-expand-${lead._id}`} onClick={() => setOpen((o) => !o)} className="rounded-lg bg-white/5 p-1 hover:bg-white/10"><CaretDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} /></button>
          </div>
        </div>
      </div>
      {open && (
        <div className="mt-3 space-y-3 border-t border-white/10 pt-3">
          {lead.problem && <p className="text-sm text-muted-foreground">"{lead.problem}"</p>}
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Set status</label>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {statuses.map((s) => (
                <button key={s} data-testid={`set-status-${lead._id}-${s.replace(/\s+/g, "-")}`} onClick={() => setStatus(s)}
                  className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${lead.status === s ? (STATUS_COLOR[s] || "bg-primary text-primary-foreground") : "bg-white/5 text-muted-foreground hover:bg-white/10"}`}>{s}</button>
              ))}
            </div>
          </div>
          <div className="flex gap-2">
            <input data-testid={`lead-note-${lead._id}`} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a follow-up note…"
              className="flex-1 rounded-lg border border-white/10 bg-background px-3 py-2 text-xs outline-none focus:border-primary" />
            <button data-testid={`lead-note-save-${lead._id}`} onClick={addNote} className="rounded-lg bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground">Add</button>
          </div>
          {(lead.notes || []).length > 0 && (
            <ul className="space-y-1 text-xs text-muted-foreground">
              {lead.notes.map((n, i) => <li key={i}>• {n.text} <span className="opacity-60">— {n.by}</span></li>)}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export default function AdminLeads() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const { data } = useQuery({
    queryKey: ["admin-leads", status, q],
    queryFn: async () => (await api.get(`/admin/leads?status=${status}&q=${encodeURIComponent(q)}`)).data,
    refetchInterval: 30000,
  });
  const leads = data?.leads || [];
  const statuses = data?.statuses || [];
  const counts = data?.counts || {};
  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-leads"] });

  return (
    <div className="space-y-5">
      <div>
        <h1 className="flex items-center gap-2 font-display text-3xl font-semibold tracking-tight"><Target size={26} weight="bold" className="text-primary" /> Leads</h1>
        <p className="mt-1 text-sm text-muted-foreground">Every appointment request and enquiry from iyeneh.com/book. Search, update status, and follow up.</p>
      </div>

      <div className="relative">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input data-testid="lead-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, business, email, phone…"
          className="w-full rounded-xl border border-white/10 bg-card py-2.5 pl-9 pr-4 text-sm outline-none focus:border-primary" />
      </div>

      <div className="flex flex-wrap gap-2" data-testid="lead-status-filters">
        {["all", ...statuses].map((s) => (
          <button key={s} data-testid={`lead-filter-${s.replace(/\s+/g, "-")}`} onClick={() => setStatus(s)}
            className={`rounded-full px-3 py-1.5 text-xs font-semibold ${status === s ? "bg-primary text-primary-foreground" : "bg-white/5 text-muted-foreground hover:bg-white/10"}`}>
            {s === "all" ? "All" : s} <span className="ml-1 opacity-70">{counts[s] ?? 0}</span>
          </button>
        ))}
      </div>

      {leads.length === 0 ? (
        <p className="rounded-xl border border-white/10 bg-card px-4 py-10 text-center text-sm text-muted-foreground" data-testid="leads-empty">No leads yet. New submissions from your booking form appear here instantly.</p>
      ) : (
        <div className="space-y-2">{leads.map((l) => <LeadRow key={l._id} lead={l} statuses={statuses} refresh={refresh} />)}</div>
      )}
    </div>
  );
}
